import axios from 'axios';
import { galleryStub } from '../data/galleryStub';
import { logError } from '../utils/errorHandlers';

const sortByDateDesc = (a, b) => new Date(b.date) - new Date(a.date);

const normalizeSortOrder = (value) => {
    // Number(null) und Number('') waeren 0 und wuerden "sortOrder: null" (Legacy,
    // kein sortOrder) nach ganz oben sortieren — statt "null ans Ende" wie im
    // Backend (services/gallery/ordering.mjs) und in docs/sortOrder_SPEC.md.
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const sortAlbumPhotos = (photos = []) => {
    const list = Array.isArray(photos) ? [...photos] : [];
    return list.sort((a, b) => {
        const aOrder = normalizeSortOrder(a?.sortOrder);
        const bOrder = normalizeSortOrder(b?.sortOrder);

        const aHas = aOrder !== null;
        const bHas = bOrder !== null;

        if (aHas && bHas && aOrder !== bOrder) return aOrder - bOrder;
        if (aHas !== bHas) return aHas ? -1 : 1;

        // Stable fallback for legacy data without sortOrder
        const aUploaded = typeof a?.uploadedAt === 'string' ? a.uploadedAt : '';
        const bUploaded = typeof b?.uploadedAt === 'string' ? b.uploadedAt : '';
        if (aUploaded !== bUploaded) return aUploaded.localeCompare(bUploaded);

        const aId = typeof a?.id === 'string' ? a.id : '';
        const bId = typeof b?.id === 'string' ? b.id : '';
        return aId.localeCompare(bId);
    });
};

const mapAlbumWithCover = (album, photos) => {
    const coverPhoto = photos.find((photo) => photo.id === album.coverPhotoId) ||
        photos.find((photo) => photo.albumId === album.id);

    return {
        ...album,
        coverPhoto,
        photoCount: photos.filter((photo) => photo.albumId === album.id).length
    };
};

const buildStubAlbums = () =>
    galleryStub.albums
        .map((album) => mapAlbumWithCover(album, galleryStub.photos))
        .sort(sortByDateDesc);

const buildStubAlbumById = (albumId) => {
    const album = galleryStub.albums.find((item) => item.id === albumId);
    if (!album) return null;

    const photos = sortAlbumPhotos(galleryStub.photos.filter((photo) => photo.albumId === albumId));

    return {
        ...album,
        photos
    };
};

const getGalleryApiBaseUrl = () => {
    const base = process.env.REACT_APP_GALLERY_API_BASE_URL;
    if (!base) return null;
    return base.endsWith('/') ? base.slice(0, -1) : base;
};

const getPreferredTimezone = () => process.env.REACT_APP_GALLERY_TIMEZONE || 'Europe/Berlin';

const resolveAuthToken = (overrideToken) => overrideToken;

const ensureGalleryApiBaseUrl = () => {
    const base = getGalleryApiBaseUrl();
    if (!base) {
        throw new Error('Missing REACT_APP_GALLERY_API_BASE_URL environment variable');
    }
    return base;
};

const buildApiUrl = (path, searchParams) => {
    const base = ensureGalleryApiBaseUrl();
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const url = new URL(`${base}${normalizedPath}`);
    if (searchParams && typeof searchParams === 'object') {
        Object.entries(searchParams).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                url.searchParams.set(key, value);
            }
        });
    }
    return url.toString();
};

const buildAuthHeaders = (authToken) => {
    const token = resolveAuthToken(authToken);
    if (!token) return {};
    return { 'Authorization': `Bearer ${token}` };
};

const mapApiAlbumSummary = (album) => {
    if (!album) return null;
    const coverPhoto = album.coverPhoto || (album.photos ? album.photos.find((photo) => photo.id === album.coverPhotoId) : null);
    return {
        ...album,
        coverPhoto,
        photoCount: album.photoCount ?? album.photos?.length ?? 0
    };
};

const mapApiAlbumDetail = (album) => {
    if (!album) return null;
    const sortedPhotos = sortAlbumPhotos(album.photos);
    return {
        ...album,
        photos: sortedPhotos
    };
};

export const fetchGalleryAlbums = async (options = {}) => {
    const base = getGalleryApiBaseUrl();
    if (!base) {
        return buildStubAlbums();
    }

    const searchParams = { timezone: getPreferredTimezone() };
    if (options.includeUnpublished) {
        searchParams.includeUnpublished = 'true';
    }

    const shouldUseAuth = Boolean(options.includeUnpublished || options.authToken);

    try {
        const response = await axios.get(buildApiUrl('/api/gallery/albums', searchParams), {
            headers: shouldUseAuth ? buildAuthHeaders(options.authToken) : {}
        });
        // Backend liefert aktuell ein nacktes Array, nicht { albums: [...] }
        const raw = Array.isArray(response?.data) ? response.data : response?.data?.albums;
        const albums = Array.isArray(raw) ? raw : [];
        return albums.map((album) => mapApiAlbumSummary(album));
    } catch (error) {
        logError('[galleryService] Failed to fetch albums from API', error);
        // Don't fall back to stub data for admin requests (they need real data)
        const shouldFallback = options.fallbackToStub !== false && !options.authToken && !options.includeUnpublished;
        if (!shouldFallback) {
            throw error;
        }
        return buildStubAlbums();
    }
};

export const fetchAlbumById = async (albumId, options = {}) => {
    if (!albumId) {
        throw new Error('[galleryService] albumId is required');
    }

    const base = getGalleryApiBaseUrl();
    if (!base) {
        const fallback = buildStubAlbumById(albumId);
        if (fallback) return fallback;
        throw new Error('Album nicht gefunden');
    }

    try {
        const response = await axios.get(buildApiUrl(`/api/gallery/albums/${albumId}`, { timezone: getPreferredTimezone() }), {
            headers: buildAuthHeaders(options.authToken)
        });
        return mapApiAlbumDetail(response?.data);
    } catch (error) {
        logError(`[galleryService] Failed to load album ${albumId} from API`, error);
        // Don't fall back to stub data for admin requests (they need real data)
        const shouldFallback = options.fallbackToStub !== false && !options.authToken;
        if (!shouldFallback) {
            throw error;
        }
        const fallback = buildStubAlbumById(albumId);
        if (fallback) return fallback;
        throw error;
    }
};

export const createGalleryAlbum = async (payload = {}, options = {}) => {
    const url = buildApiUrl('/api/gallery/albums');
    const response = await axios.post(url, payload, {
        headers: buildAuthHeaders(options.authToken)
    });
    return response?.data;
};

export const updateGalleryAlbum = async (albumId, payload = {}, options = {}) => {
    if (!albumId) {
        throw new Error('[galleryService] albumId is required for updates');
    }
    const url = buildApiUrl(`/api/gallery/albums/${albumId}`);
    const response = await axios.patch(url, payload, {
        headers: buildAuthHeaders(options.authToken)
    });
    return response?.data;
};

export const deleteGalleryAlbum = async (albumId, options = {}) => {
    if (!albumId) {
        throw new Error('[galleryService] albumId is required for album deletion');
    }
    const url = buildApiUrl(`/api/gallery/albums/${albumId}`);
    const response = await axios.delete(url, {
        headers: buildAuthHeaders(options.authToken),
        data: { confirm: 'DELETE' }
    });
    return response?.data;
};

export const generateUploadUrls = async (albumId, photos = [], options = {}) => {
    if (!albumId) {
        throw new Error('[galleryService] albumId is required for upload URLs');
    }
    if (!Array.isArray(photos) || photos.length === 0) {
        throw new Error('[galleryService] photos payload is required for upload URLs');
    }

    const url = buildApiUrl(`/api/gallery/albums/${albumId}/photos/upload-urls`);
    const response = await axios.post(
        url,
        { photos },
        { headers: buildAuthHeaders(options.authToken) }
    );
    return response?.data;
};

export const processUploadedPhotos = async (albumId, photos = [], options = {}) => {
    if (!albumId) {
        throw new Error('[galleryService] albumId is required for processing uploads');
    }
    if (!Array.isArray(photos) || photos.length === 0) {
        throw new Error('[galleryService] photos payload is required for processing');
    }

    const url = buildApiUrl(`/api/gallery/albums/${albumId}/photos/process-uploads`);
    const response = await axios.post(
        url,
        { photos },
        { headers: buildAuthHeaders(options.authToken) }
    );
    return response?.data;
};

export const updateGalleryPhoto = async (albumId, photoId, payload = {}, options = {}) => {
    if (!albumId || !photoId) {
        throw new Error('[galleryService] albumId and photoId are required for photo updates');
    }
    const url = buildApiUrl(`/api/gallery/albums/${albumId}/photos/${photoId}`);
    const response = await axios.patch(url, payload, {
        headers: buildAuthHeaders(options.authToken)
    });
    return response?.data;
};

export const reorderGalleryAlbumPhotos = async (albumId, orderedPhotoIds = [], options = {}) => {
    if (!albumId) {
        throw new Error('[galleryService] albumId is required for photo reordering');
    }
    if (!Array.isArray(orderedPhotoIds) || orderedPhotoIds.length === 0) {
        throw new Error('[galleryService] orderedPhotoIds array is required for photo reordering');
    }

    const url = buildApiUrl(`/api/gallery/albums/${albumId}/photos/reorder`);
    const response = await axios.post(
        url,
        { orderedPhotoIds },
        { headers: buildAuthHeaders(options.authToken) }
    );

    // Backend shape: { album: { ... } }
    if (response?.data?.album) {
        return { ...response.data, album: mapApiAlbumDetail(response.data.album) };
    }

    // Defensive fallback: in case backend returns the raw album directly.
    if (response?.data?.id && Array.isArray(response?.data?.photos)) {
        return { album: mapApiAlbumDetail(response.data) };
    }

    return response?.data;
};

export const deleteGalleryPhotos = async (albumId, photoIds = [], options = {}) => {
    if (!albumId) {
        throw new Error('[galleryService] albumId is required for photo deletions');
    }
    const ids = Array.isArray(photoIds) ? photoIds : [photoIds];
    if (!ids.length) {
        throw new Error('[galleryService] at least one photoId must be provided for deletion');
    }
    const url = buildApiUrl(`/api/gallery/albums/${albumId}/photos/delete`);
    const response = await axios.post(
        url,
        { photoIds: ids },
        { headers: buildAuthHeaders(options.authToken) }
    );
    return response?.data;
};

export const moveGalleryPhotos = async (albumId, photoIds = [], targetAlbumId, options = {}) => {
    if (!albumId) {
        throw new Error('[galleryService] albumId is required for photo move');
    }
    if (!targetAlbumId) {
        throw new Error('[galleryService] targetAlbumId is required for photo move');
    }
    const ids = Array.isArray(photoIds) ? photoIds : [photoIds];
    if (!ids.length) {
        throw new Error('[galleryService] at least one photoId must be provided for move');
    }
    const url = buildApiUrl(`/api/gallery/albums/${albumId}/photos/move`);
    const response = await axios.post(
        url,
        { photoIds: ids, targetAlbumId },
        { headers: buildAuthHeaders(options.authToken) }
    );
    return response?.data;
};

export const fetchTodayUploadSuggestions = async (options = {}) => {
    const url = buildApiUrl('/api/gallery/upload-suggestions/today');
    const response = await axios.get(url, {
        headers: buildAuthHeaders(options.authToken)
    });

    // Backend returns an array directly.
    if (Array.isArray(response?.data)) {
        return response.data;
    }

    // Backward/alternative shape.
    return Array.isArray(response?.data?.suggestions) ? response.data.suggestions : [];
};
